import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldIcon, UsersIcon, KeyIcon, BuildingIcon, NetworkIcon, FileCheckIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function ICAMCapabilitiesPage() {
  return <div className="w-full" data-id="element-1381" data-mp="1twkszcw4dmsg">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern py-12" data-id="element-1382" data-mp="1ty5op5u67brw">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1383" data-mp="1yumfk7sr848o">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-1384" data-mp="22x99qfcwe191">
            <div className="text-sm font-bold text-nrs-light mb-4 uppercase tracking-wider" data-id="element-1385" data-mp="22zc93pzxx6qf">
              <Link to="/services" className="hover:text-nrs-orange" data-id="element-1386" data-mp="27tzp10gvoste">
                Services
              </Link>{' '}
              &gt;{' '}
              <Link to="/services/identity-access-management" className="hover:text-nrs-orange" data-id="element-1387" data-mp="3jag8d4vjztlj">
                ICAM
              </Link>{' '}
              &gt; Capabilities
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" data-id="element-1388" data-mp="3t88nj8niwd7b">
              ICAM Capabilities in Depth
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange rounded-full mb-6" data-id="element-1389" data-mp="3o841z47rcsa8"></div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 1: Zero Trust */}
      <section className="section-white border-b border-gray-200" data-id="element-1390" data-mp="3f9ikzdhxplbk">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1391" data-mp="3fatordbdqt2o">
          <div className="flex items-center gap-4 mb-8" data-id="element-1392" data-mp="3agzaipejn6az">
            <ShieldIcon className="h-12 w-12 text-nrs-orange" data-id="element-1393" data-mp="35ieaapzakmmj" />
            <h2 className="text-3xl font-bold" data-id="element-1394" data-mp="35guqipefftg0">
              Zero Trust Architecture Implementation
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1395" data-mp="2vl9leie2ad21">
            <div className="lg:col-span-2 space-y-6 text-gray-600 leading-relaxed" data-id="element-1396" data-mp="2rjel9h7va5pu">
              <p data-id="element-1397" data-mp="2rliwylc0c90o">
                Zero Trust is a security model that eliminates implicit trust
                and continuously validates every stage of digital interaction.
                New River Systems designs and implements Zero Trust
                architectures aligned with NIST 800-207 and OMB M-22-09.
              </p>
              <p data-id="element-1398" data-mp="2cw7i3xomic1i">
                Our technical approach shifts the security perimeter from the
                network to the identity. We integrate your existing security
                infrastructure with modern identity platforms to ensure that
                access is granted based on continuous evaluation of user
                context, device health, and risk signals.
              </p>

              <h3 className="text-xl font-bold text-nrs-navy mt-8 mb-4" data-id="element-1399" data-mp="2vv1x97s01ei7">
                Technical Approach:
              </h3>
              <ul className="space-y-2 list-disc ml-5" data-id="element-1400" data-mp="3jke440zutahc">
                <li data-id="element-1401" data-mp="3obw8kz135t72">
                  Identity as the perimeter (verify identity, not network
                  location)
                </li>
                <li data-id="element-1402" data-mp="1jzbjogx3kvy">Least privilege access enforcement</li>
                <li data-id="element-1403" data-mp="3tpcmu9ui4421">Explicit verification for every access request</li>
                <li data-id="element-1404" data-mp="3tn08e6nycbtq">Assume breach mentality and lateral movement prevention</li>
                <li data-id="element-1405" data-mp="balofax91xay">Data classification and protection</li>
              </ul>
            </div>

            <div className="space-y-8" data-id="element-1406" data-mp="6ck5cpztpxzh">
              <div className="bg-nrs-light p-6 rounded-lg" data-id="element-1407" data-mp="3eru35lwokh1g">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-1408" data-mp="3esc4gwy79lg8">
                  What We Deliver:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-1409" data-mp="3aslakkddmp7h">
                  <li className="flex items-start gap-2" data-id="element-1410" data-mp="3orkx9mhbwu10">
                    <span className="text-nrs-orange" data-id="element-1411" data-mp="3jqdpvoj3lwuv">•</span> Zero Trust
                    maturity assessment
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1412" data-mp="13yhehwsbcqyr">
                    <span className="text-nrs-orange" data-id="element-1413" data-mp="z0y0mxzj2g7c">•</span> Identity-centric
                    architecture design
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1414" data-mp="34q9rs7m9q31e">
                    <span className="text-nrs-orange" data-id="element-1415" data-mp="39ntbvo5nda19">•</span>{' '}
                    Micro-segmentation strategy
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1416" data-mp="3emcxtok8qltm">
                    <span className="text-nrs-orange" data-id="element-1417" data-mp="3iohpwiv1jae5">•</span> Continuous
                    authentication policies
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1418" data-mp="3nn1b2h1t8i5g">
                    <span className="text-nrs-orange" data-id="element-1419" data-mp="3smnp2spgq2fb">•</span> Integration with
                    SIEM & SOAR
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1420" data-mp="19km0nuiexcs">
                    <span className="text-nrs-orange" data-id="element-1421" data-mp="672ygo7qjupj">•</span> Monitoring and
                    analytics dashboards
                  </li>
                </ul>
              </div>

              <div className="border border-gray-200 p-6 rounded-lg" data-id="element-1422" data-mp="a73ak27d3ttf">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-1423" data-mp="f8k9hs6ad67d">
                  Technologies Used:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-1424" data-mp="1znp4q8uw20ex">
                  <li data-id="element-1425" data-mp="1upf7lsplspqo">
                    <strong data-id="element-1426" data-mp="1ut34n1u9bua5">Identity:</strong> OKTA, Azure AD, Ping Identity
                  </li>
                  <li data-id="element-1427" data-mp="1qrhjkt7kk9ov">
                    <strong data-id="element-1428" data-mp="1lqadcyl2rikb">Micro-segmentation:</strong> Illumio, VMware NSX
                  </li>
                  <li data-id="element-1429" data-mp="2igji4ycv5kh1">
                    <strong data-id="element-1430" data-mp="2igszxynr8lbn">Policy:</strong> Palo Alto Networks, Cisco ISE
                  </li>
                  <li data-id="element-1431" data-mp="2eepmx5lft8o9">
                    <strong data-id="element-1432" data-mp="2eg920svcxdog">Monitoring:</strong> Splunk, Azure Sentinel
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: IAG */}
      <section className="section-white border-b border-gray-200 bg-gray-50" data-id="element-1433" data-mp="24oweueki7bor">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1434" data-mp="325bs8p424euk">
          <div className="flex items-center gap-4 mb-8" data-id="element-1435" data-mp="2xdk1oekwi94f">
            <UsersIcon className="h-12 w-12 text-nrs-orange" data-id="element-1436" data-mp="2xbz74oyl6q6k" />
            <h2 className="text-3xl font-bold" data-id="element-1437" data-mp="3gbdgaqj4lm28">Identity & Access Governance</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1438" data-mp="3l7u6dq9vyahy">
            <div className="lg:col-span-2 space-y-6 text-gray-600 leading-relaxed" data-id="element-1439" data-mp="37ia92zz2q5g1">
              <p data-id="element-1440" data-mp="3bjc93j0telj2">
                Identity and Access Governance (IAG) ensures the right people
                have the right access to the right resources at the right
                time—and can prove it. Without strong governance, agencies
                accumulate orphaned accounts, over-provisioned users, and
                compliance violations.
              </p>
              <p data-id="element-1441" data-mp="2okzbrkx05m38">
                We guide agencies through the transformation from their "as-is"
                state to a policy-driven "to-be" architecture. Our approach
                establishes clear ownership of identity data and automates the
                lifecycle of user access from onboarding through separation.
              </p>

              <h3 className="text-xl font-bold text-nrs-navy mt-8 mb-4" data-id="element-1442" data-mp="2badzhenfaqtg">
                Technical Approach:
              </h3>
              <ul className="space-y-2 list-disc ml-5" data-id="element-1443" data-mp="27b54k6gncmwb">
                <li data-id="element-1444" data-mp="2l1ic47pmlzl4">
                  <strong data-id="element-1445" data-mp="2l20g5qij5i3l">Discover:</strong> Inventory all identities and
                  entitlements
                </li>
                <li data-id="element-1446" data-mp="2g8p51d6oqf5v">
                  <strong data-id="element-1447" data-mp="2g8fgzv57ai2t">Define:</strong> Establish governance policies and
                  roles
                </li>
                <li data-id="element-1448" data-mp="2q0bqe2cqlumc">
                  <strong data-id="element-1449" data-mp="2q0uxeo0umyll">Deploy:</strong> Implement IAG platform (SailPoint,
                  Saviynt, etc.)
                </li>
                <li data-id="element-1450" data-mp="33zb5ojed0x5u">
                  <strong data-id="element-1451" data-mp="33xgtpul2gfdw">Operate:</strong> Run certification campaigns and
                  monitor compliance
                </li>
              </ul>
            </div>

            <div className="space-y-8" data-id="element-1452" data-mp="19mlcy4zq9u01">
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm" data-id="element-1453" data-mp="14mqixsknooar">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-1454" data-mp="14p39gu4hc6zf">
                  What We Deliver:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-1455" data-mp="nse7cx6g7z55">
                  <li className="flex items-start gap-2" data-id="element-1456" data-mp="nqjmtjz3a91l">
                    <span className="text-nrs-orange" data-id="element-1457" data-mp="ske4yiz1qfyk">•</span> Identity
                    governance framework
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1458" data-mp="xkk0494iykle">
                    <span className="text-nrs-orange" data-id="element-1459" data-mp="t8c8nlnnkjb">•</span> Role-Based Access
                    Control (RBAC) design
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1460" data-mp="4vuy87vnab8l">
                    <span className="text-nrs-orange" data-id="element-1461" data-mp="9rto5zwmg1b4">•</span> Access
                    certification processes
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1462" data-mp="epv80n1vg4nn">
                    <span className="text-nrs-orange" data-id="element-1463" data-mp="1l6yvjh7p6o12">•</span> Attribute-based
                    access control (ABAC)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1464" data-mp="1q97pafse9u48">
                    <span className="text-nrs-orange" data-id="element-1465" data-mp="3ao4793gyph02">•</span> Segregation of
                    Duties (SoD) enforcement
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1466" data-mp="35sf30mtzzbxf">
                    <span className="text-nrs-orange" data-id="element-1467" data-mp="30y1ft7phob3u">•</span> Orphaned account
                    detection
                  </li>
                </ul>
              </div>

              <div className="bg-nrs-navy text-white p-6 rounded-lg" data-id="element-1468" data-mp="2rz7bsw9twig9">
                <h3 className="text-lg font-bold mb-4" data-id="element-1469" data-mp="2rwbe991w7vsc">Technologies Used:</h3>
                <ul className="space-y-2 text-sm text-blue-100" data-id="element-1470" data-mp="2my9t8ikwpme8">
                  <li data-id="element-1471" data-mp="2i1jfi1wkr2vz">SailPoint IdentityIQ / IdentityNow</li>
                  <li data-id="element-1472" data-mp="2d1exu8jxk246">Saviynt</li>
                  <li data-id="element-1473" data-mp="2d26vrq102bxm">Microsoft Azure AD Governance</li>
                  <li data-id="element-1474" data-mp="2d3rqbfnbduvh">Custom governance workflows</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: Credential Management */}
      <section className="section-white border-b border-gray-200" data-id="element-1475" data-mp="b87m193j02n1">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1476" data-mp="2mvfizjy3bvtu">
          <div className="flex items-center gap-4 mb-8" data-id="element-1477" data-mp="2hyp5n4do7ec4">
            <KeyIcon className="h-12 w-12 text-nrs-orange" data-id="element-1478" data-mp="2i0a0yw7t71b1" />
            <h2 className="text-3xl font-bold" data-id="element-1479" data-mp="2wuo89zi0gm9v">
              Credential Management & Single Sign-On
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1480" data-mp="2ru8y1gt0zuw4">
            <div className="lg:col-span-2 space-y-6 text-gray-600 leading-relaxed" data-id="element-1481" data-mp="2470qes84k9qx">
              <p data-id="element-1482" data-mp="1z8qw0ju85d6d">
                Strong authentication is the cornerstone of federal
                cybersecurity. We help agencies move beyond passwords to
                implement phishing-resistant credentials and seamless Single
                Sign-On (SSO) experiences that improve both security and user
                productivity.
              </p>
              <p data-id="element-1483" data-mp="1gh6f4f3get8s">
                Our solutions integrate PIV/CAC cards, derived credentials for
                mobile devices, and modern FIDO2 authenticators into a unified
                access management strategy that satisfies OMB M-22-09
                requirements.
              </p>

              <h3 className="text-xl font-bold text-nrs-navy mt-8 mb-4" data-id="element-1484" data-mp="3i63vh8676fv7">
                Technical Approach:
              </h3>
              <ul className="space-y-2 list-disc ml-5" data-id="element-1485" data-mp="3n4f4xhskyocw">
                <li data-id="element-1486" data-mp="3n2uarta6h7fk">
                  Assess current authentication landscape and legacy
                  applications
                </li>
                <li data-id="element-1487" data-mp="joq6uyipwhop">Design centralized identity provider (IdP) architecture</li>
                <li data-id="element-1488" data-mp="5p8fqchy8sc6">Implement federation protocols (SAML, OIDC, OAuth)</li>
                <li data-id="element-1489" data-mp="5r1lzqonhajn">Deploy adaptive, risk-based authentication policies</li>
              </ul>
            </div>

            <div className="space-y-8" data-id="element-1490" data-mp="2qexwww64kq81">
              <div className="bg-nrs-light p-6 rounded-lg" data-id="element-1491" data-mp="2qe4uu8i42ow8">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-1492" data-mp="2ug9np50qjhht">
                  What We Deliver:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-1493" data-mp="10wr68ucq4qm1">
                  <li className="flex items-start gap-2" data-id="element-1494" data-mp="vypm063kal8z">
                    <span className="text-nrs-orange" data-id="element-1495" data-mp="vz7srwnuohv5">•</span> PIV/CAC card
                    integration and middleware
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1496" data-mp="m963j1jrgten">
                    <span className="text-nrs-orange" data-id="element-1497" data-mp="m8mlzly0w715">•</span> Derived
                    credential issuance
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1498" data-mp="d87zwkd8inwl">
                    <span className="text-nrs-orange" data-id="element-1499" data-mp="d6n4ytn0d2y7">•</span> FIDO2 / WebAuthn
                    passwordless auth
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1500" data-mp="3a1z6635zntb">
                    <span className="text-nrs-orange" data-id="element-1501" data-mp="3blg7ywna2w5">•</span> SSO portal and
                    federation
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1502" data-mp="22ks7m1g3r2ta">
                    <span className="text-nrs-orange" data-id="element-1503" data-mp="1xpcw3g5khj01">•</span> Step-up
                    authentication for high-risk
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1504" data-mp="263950tocqiid">
                    <span className="text-nrs-orange" data-id="element-1505" data-mp="2a5cifnukzx6a">•</span>{' '}
                    Phishing-resistant MFA deployment
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: PACS + LACS */}
      <section className="section-white border-b border-gray-200 bg-gray-50" data-id="element-1506" data-mp="1n6zhzgvh2hm8">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1507" data-mp="1r7jld26n2hgt">
          <div className="flex items-center gap-4 mb-8" data-id="element-1508" data-mp="1r9mhm3ydx6tz">
            <BuildingIcon className="h-12 w-12 text-nrs-orange" data-id="element-1509" data-mp="1w64k0x68st35" />
            <h2 className="text-3xl font-bold" data-id="element-1510" data-mp="215pfuyq48lbn">
              Physical & Logical Access Integration (PACS + LACS)
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1511" data-mp="37ljd3pvbz0sz">
            <div className="lg:col-span-2 space-y-6 text-gray-600 leading-relaxed" data-id="element-1512" data-mp="3ckctbq2055wu">
              <p data-id="element-1513" data-mp="pc6p4y7w4dkz">
                True enterprise security requires the convergence of physical
                and cyber security. We unify Physical Access Control Systems
                (PACS) with Logical Access Control Systems (LACS) to ensure that
                a user's physical presence aligns with their network access
                rights.
              </p>
              <p data-id="element-1514" data-mp="tgmt2txy1axk">
                When an employee is terminated, their physical badge and network
                access should be revoked simultaneously. We build the
                integrations and workflows to make this a reality, closing
                critical security gaps.
              </p>

              <h3 className="text-xl font-bold text-nrs-navy mt-8 mb-4" data-id="element-1515" data-mp="1h5pdg9jyphvr">
                Technical Approach:
              </h3>
              <ul className="space-y-2 list-disc ml-5" data-id="element-1516" data-mp="1r1isbzl5lngp">
                <li data-id="element-1517" data-mp="1qzy2uodp4upp">Map physical access zones to logical access roles</li>
                <li data-id="element-1518" data-mp="1m4ipdxjlp0tt">
                  Integrate PACS databases (Lenel, Hirsch, etc.) with identity
                  governance platforms
                </li>
                <li data-id="element-1519" data-mp="2aodq40g6kle5">Automate badge lifecycle events based on HR triggers</li>
                <li data-id="element-1520" data-mp="1vqlbdaigk110">Implement location-based conditional access policies</li>
              </ul>
            </div>

            <div className="space-y-8" data-id="element-1521" data-mp="20peficbuufoi">
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm" data-id="element-1522" data-mp="20oczrujw950g">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-1523" data-mp="1mx88bfqojj17">
                  What We Deliver:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-1524" data-mp="1rqavn64v5w8y">
                  <li className="flex items-start gap-2" data-id="element-1525" data-mp="1d14xllhric2n">
                    <span className="text-nrs-orange" data-id="element-1526" data-mp="1hz6f41zji3c0">•</span> Badge management
                    lifecycle automation
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1527" data-mp="140gjyrwa6dqa">
                    <span className="text-nrs-orange" data-id="element-1528" data-mp="180zbsly6bjsh">•</span> Physical access
                    control system integration
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1529" data-mp="3aoftte5qdcsa">
                    <span className="text-nrs-orange" data-id="element-1530" data-mp="35x7k0h0hnpel">•</span> Visitor
                    management systems
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1531" data-mp="30yw7tzmq12t7">
                    <span className="text-nrs-orange" data-id="element-1532" data-mp="2vyrsicxjue4k">•</span> Convergence of
                    physical and cyber security
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1533" data-mp="3tl797ci5j7r2">
                    <span className="text-nrs-orange" data-id="element-1534" data-mp="3onoxjecr2efd">•</span> Insider threat
                    monitoring integration
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: Platform Integration */}
      <section className="section-white border-b border-gray-200" data-id="element-1535" data-mp="28976fl63n0r2">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1536" data-mp="28aia7kzjo8i6">
          <div className="flex items-center gap-4 mb-8" data-id="element-1537" data-mp="246iuv5q03fqe">
            <NetworkIcon className="h-12 w-12 text-nrs-orange" data-id="element-1538" data-mp="jpjillt8zdk1" />
            <h2 className="text-3xl font-bold" data-id="element-1539" data-mp="jr49n1gcsed5">
              Identity Platform Integration & Engineering
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1540" data-mp="sq7ti7ncgxsp">
            <div className="lg:col-span-2 space-y-6 text-gray-600 leading-relaxed" data-id="element-1541" data-mp="sowpq7twfq1l">
              <p data-id="element-1542" data-mp="xiiu7cpwsnmb">
                Identity platforms are only as valuable as the systems they
                connect to. Our engineering teams specialize in integrating
                complex, legacy, and custom federal applications with modern
                identity infrastructure.
              </p>
              <p data-id="element-1543" data-mp="1bhik0np5r8k2">
                Whether deploying on-premise in secure environments, in FedRAMP
                cloud environments, or in hybrid architectures, we build the
                connectors, APIs, and synchronization engines that keep identity
                data accurate across the enterprise.
              </p>

              <h3 className="text-xl font-bold text-nrs-navy mt-8 mb-4" data-id="element-1544" data-mp="1nzbhonzqcv4n">
                Technical Approach:
              </h3>
              <ul className="space-y-2 list-disc ml-5" data-id="element-1545" data-mp="1jy9ddsqyhsvt">
                <li data-id="element-1546" data-mp="1jvn8y1zd3tht">
                  Develop custom connectors for legacy/proprietary systems
                </li>
                <li data-id="element-1547" data-mp="1sq1l3volinwp">Implement robust identity data synchronization engines</li>
                <li data-id="element-1548" data-mp="1ss4l98jgpxf5">
                  Design fault-tolerant, highly available identity architectures
                </li>
                <li data-id="element-1549" data-mp="10yvkzf8vkq7f">Automate identity workflows via REST/SOAP APIs</li>
              </ul>
            </div>

            <div className="space-y-8" data-id="element-1550" data-mp="1eyde26t7epml">
              <div className="bg-nrs-light p-6 rounded-lg" data-id="element-1551" data-mp="1ezg4kdmm6q1z">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-1552" data-mp="19z0q1iqljcif">
                  What We Deliver:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-1553" data-mp="2qcqs6jy5nqpf">
                  <li className="flex items-start gap-2" data-id="element-1554" data-mp="2lez2lwkzggoe">
                    <span className="text-nrs-orange" data-id="element-1555" data-mp="2leh0ijbn388k">•</span> Platform
                    deployment (on-prem, cloud, hybrid)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1556" data-mp="c6kagzxtuunw">
                    <span className="text-nrs-orange" data-id="element-1557" data-mp="c3y3p2irgj6q">•</span> Custom connector
                    development
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1558" data-mp="352ztx36vl6l">
                    <span className="text-nrs-orange" data-id="element-1559" data-mp="35mcozdzfjdr">•</span> Identity data
                    synchronization
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1560" data-mp="uwadgabqumqw">
                    <span className="text-nrs-orange" data-id="element-1561" data-mp="uvqucqacxsba">•</span> Workflow
                    automation
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1562" data-mp="h3ax66450oia">
                    <span className="text-nrs-orange" data-id="element-1563" data-mp="m2xb6hrsi8s5">•</span> API integration
                    with HR, AD, cloud apps
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Compliance */}
      <section className="section-white border-b border-gray-200 bg-gray-50" data-id="element-1564" data-mp="1uv1txsjgi90m">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1565" data-mp="1ztmvbvag2yqn">
          <div className="flex items-center gap-4 mb-8" data-id="element-1566" data-mp="1zuxx5pkbxwf4">
            <FileCheckIcon className="h-12 w-12 text-nrs-orange" data-id="element-1567" data-mp="1m0x7dlbcvjqw" />
            <h2 className="text-3xl font-bold" data-id="element-1568" data-mp="1q2s7wnlgpt3m">ICAM Compliance & Assessment</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1569" data-mp="1c3smrpx5rwc7">
            <div className="lg:col-span-2 space-y-6 text-gray-600 leading-relaxed" data-id="element-1570" data-mp="1c5ltf57rugk7">
              <p data-id="element-1571" data-mp="1h3x1pbif4j0b">
                Federal identity systems must meet rigorous compliance
                standards. We ensure your ICAM architecture aligns with FICAM
                guidelines and satisfies the security controls required for
                Authority to Operate (ATO).
              </p>
              <p data-id="element-1572" data-mp="2xhnagvkhfvga">
                Our team maps technical implementations directly to NIST 800-53
                controls, generates required RMF artifacts, and provides the
                continuous monitoring capabilities needed to maintain compliance
                over time.
              </p>

              <h3 className="text-xl font-bold text-nrs-navy mt-8 mb-4" data-id="element-1573" data-mp="lrfdvuor6qkm">
                Technical Approach:
              </h3>
              <ul className="space-y-2 list-disc ml-5" data-id="element-1574" data-mp="uugj7t5sodn8">
                <li data-id="element-1575" data-mp="usn8a1o5f79f">
                  Conduct gap assessments against FICAM and NIST standards
                </li>
                <li data-id="element-1576" data-mp="19l7a8yzkb8pa">Map identity workflows to specific security controls</li>
                <li data-id="element-1577" data-mp="14jhwvcovaiy1">Automate compliance reporting and evidence gathering</li>
                <li data-id="element-1578" data-mp="1iipz6egbu76g">Support third-party assessments and audits</li>
              </ul>
            </div>

            <div className="space-y-8" data-id="element-1579" data-mp="1dlr8y6zsakdj">
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm" data-id="element-1580" data-mp="1dillew6szqjx">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-1581" data-mp="3kd8h3kdnfqhv">
                  What We Deliver:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700" data-id="element-1582" data-mp="1j8wlefnlut5x">
                  <li className="flex items-start gap-2" data-id="element-1583" data-mp="1jar3lm7hs76b">
                    <span className="text-nrs-orange" data-id="element-1584" data-mp="1o77u2n25yxmk">•</span> NIST 800-53
                    control mapping
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1585" data-mp="1t5j531473e6d">
                    <span className="text-nrs-orange" data-id="element-1586" data-mp="1x97cnjstu9rm">•</span> FISMA compliance
                    reporting
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1587" data-mp="226zaeug5kq3q">
                    <span className="text-nrs-orange" data-id="element-1588" data-mp="276lof63t2adl">•</span> RMF artifacts
                    (SSP, SAR, POA&M)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1589" data-mp="2bywpwde72489">
                    <span className="text-nrs-orange" data-id="element-1590" data-mp="2gwf1kbjlixjy">•</span> Continuous
                    monitoring dashboards
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1591" data-mp="2kzl3x83l84l5">
                    <span className="text-nrs-orange" data-id="element-1592" data-mp="2px3fl68zoxwu">•</span> Annual
                    assessments and audits
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-navy" data-id="element-1593" data-mp="99ddkoygbvxx">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1594" data-mp="591s59y9lrle">
          <h3 className="text-3xl font-bold mb-8 text-white" data-id="element-1595" data-mp="5b63ue2enuw8">
            Ready to Implement World-Class ICAM?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1596" data-mp="x10vihdqjwo2">
            <Link to="/contact-us" className="btn-primary" data-id="element-1597" data-mp="syw3to6ula42">
              Request an Assessment
            </Link>
            <Link to="/services/identity-access-management" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-1598" data-mp="o7xmcla4i5z8">
              Back to ICAM Services
            </Link>
          </div>
        </div>
      </section>
    </div>;
}